/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// The contextual-help topic registry is GENERATED from the docs site — see
// docs/DOCS_HELP_REGISTRY.md. Titles and heading anchors mirror apps/docs/docs,
// as do the excerpts in the file beside it; this module adds the URL builder
// and the docsHelp() resolver on top.
// Regenerate with: node tools/scripts/generate-docs-help.mjs
import { createElement, type ReactNode } from 'react'
import DocsHelpExcerpt from '../components/docs-help-excerpt.component'
import {
  DOCS_HELP_ANCHORS,
  type DocsHelpAnchor,
  DOCS_HELP_TOPICS,
  type DocsHelpTopic,
  type DocsHelpTopicKey,
} from './docs-help.generated'

export {
  DOCS_HELP_ANCHORS,
  type DocsHelpAnchor,
  DOCS_HELP_TOPICS,
  type DocsHelpTopic,
  type DocsHelpTopicKey,
} from './docs-help.generated'

// The docs Vercel project deploys from apps/docs; the canonical domain lives
// in apps/docs/docusaurus.config.ts — keep the two in sync.
/**
 * The docs-site origin (AGL-2186).
 *
 * `NEXT_PUBLIC_DOCS_ORIGIN` is canonical — it is the name `.env.selfhost.example`
 * and `docs/SELF_HOSTING.md` document, so it is the one an operator has
 * actually set. `NEXT_PUBLIC_AGLYN_DOCS_URL` is the older name kept as a
 * fallback so an existing deployment configured under it keeps working.
 *
 * They were TWO independent variables reading one value: Assist citations
 * honoured the documented name while every console and besigner help link
 * honoured the undocumented one. An operator who followed the runbook exactly
 * retargeted a third of their links and left the rest pointing at our
 * documentation, presented inside their product as theirs. That is the exact
 * pattern `operator-identity.ts` warns about — "AGL-733 is the record of what
 * happens when one value acquires two env names" — with the split already in
 * place one directory over from the warning.
 *
 * Dot notation: reaches client bundles (AGL-2037).
 */
export const DOCS_BASE_URL = (
  process.env.NEXT_PUBLIC_DOCS_ORIGIN ||
  process.env.NEXT_PUBLIC_AGLYN_DOCS_URL ||
  'https://docs.aglyn.com'
).replace(/\/+$/, '')

/** Absolute docs URL for a docs-site path (docs serve from the site root). */
export function buildDocsUrl(path = '/'): string {
  return `${DOCS_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`
}

/**
 * Narrow a topic key that arrives as an untyped string — from a plugin's
 * `ConsoleNavItem.header.docsTopic` (AGL-1074) — to one the registry holds,
 * or the given fallback.
 *
 * Plugins declare the topic in `@aglyn/aglyn`, which cannot import this
 * registry (a lib importing an app), so the string is unvalidated by
 * construction, and a third-party plugin can name anything at all. Indexing
 * the registry with it would produce an `undefined` topic and a help button
 * that throws when it renders — hence a resolver rather than a cast.
 */
export function resolveDocsHelpTopic(
  topic: string | undefined,
  fallback: DocsHelpTopicKey,
): DocsHelpTopicKey {
  return topic && topic in DOCS_HELP_TOPICS
    ? (topic as DocsHelpTopicKey)
    : fallback
}

/**
 * {@link resolveDocsHelpTopic}, deep-linked: the topic a plugin names, and
 * the heading it names on that topic's page when the page carries it
 * (`ConsoleNavItem.header.docsAnchor`).
 *
 * The anchor arrives as an untyped string for the topic's reason, so it is
 * checked against the generated heading list rather than cast. One the page
 * does not carry is dropped, and so is any anchor once the topic itself fell
 * back: a heading of one page is not a heading of another, and a reader
 * sent to a missing one lands at the top believing they are in the right
 * place.
 */
export function resolveDocsHelpTarget(
  topic: string | undefined,
  anchor: string | undefined,
  fallback: DocsHelpTopicKey,
): DocsHelpTopicKey | DocsHelpTarget {
  const resolved = resolveDocsHelpTopic(topic, fallback)
  if (!anchor || resolved !== topic) return resolved
  const anchors: readonly string[] =
    (DOCS_HELP_ANCHORS as Partial<Record<DocsHelpTopicKey, readonly string[]>>)[
      resolved
    ] ?? []
  return anchors.includes(anchor)
    ? ({ topic: resolved, anchor } as DocsHelpTarget)
    : resolved
}

/**
 * A page header's help destination: a topic, optionally deep-linked to one of
 * that topic's real headings (AGL-2200).
 *
 * Written as a distributive mapped type rather than a generic interface so a
 * plain props field can carry it — `{ topic: 'billing', anchor: '#seats' }`
 * type-checks, `{ topic: 'billing', anchor: '#promote' }` does not, and no
 * component has to become generic to say so.
 *
 * It exists because `DashboardLayout` could only name a topic. Every page
 * under `/admin` therefore landed on the top of one long staff-console page,
 * and all seven Plugins/Marketplace pages opened the same "Plugins &
 * Marketplace" tooltip — the AGL-1074 shape, arrived at from the destination
 * end instead of the route end.
 */
export type DocsHelpTarget = {
  [K in DocsHelpTopicKey]: {
    topic: K
    anchor?: DocsHelpAnchor<K>
    /** Override the tooltip title (defaults to the topic's docs page title). */
    title?: string
    /** Override the tooltip excerpt (defaults to the topic's docs excerpt). */
    excerpt?: string
  }
}[DocsHelpTopicKey]

export interface DocsHelpOverrides<
  K extends DocsHelpTopicKey = DocsHelpTopicKey,
> {
  /** Heading anchor on the topic's docs page. Type-checked against the page's
   * real headings — a stale anchor is a compile error (AGL-602). */
  anchor?: DocsHelpAnchor<K>
  /** Override the tooltip title (defaults to the topic's docs page title). */
  title?: string
  /** Override the tooltip excerpt (defaults to the topic's docs excerpt). */
  excerpt?: string
}

/**
 * Resolve a registry topic (plus optional heading anchor and copy overrides)
 * into the `HelpTipContent` shape the shared UI help affordances accept
 * (AGL-600/601). The `anchor` is constrained to the topic page's real
 * headings, so a docs restructure surfaces as a type error here.
 *
 * The title and href are strings, resolved here and now: they are what a help
 * button renders BEFORE anyone hovers it — its accessible name and its
 * destination. The excerpt is a node instead, because it is read only inside
 * an open tooltip and the registry's 147 excerpts are ~20 KB of prose that
 * every console page was otherwise carrying (AGL-2706). `DocsHelpExcerpt`
 * fetches them when a tooltip mounts; `HelpTipContent.excerpt` has always
 * been a `ReactNode`, so a caller passing this straight through is unchanged.
 * An `overrides.excerpt` still short-circuits it — a card that writes its own
 * sentence never reaches the registry at all.
 */
export function docsHelp<K extends DocsHelpTopicKey>(
  topic: K,
  overrides: DocsHelpOverrides<K> = {},
): { title: string; excerpt: ReactNode; href: string } {
  const { path, title }: DocsHelpTopic = DOCS_HELP_TOPICS[topic]
  return {
    title: overrides.title ?? title,
    excerpt: overrides.excerpt ?? createElement(DocsHelpExcerpt, { topic }),
    href: `${buildDocsUrl(path)}${overrides.anchor ?? ''}`,
  }
}
